import axios from "axios";
import useAnalytics from "hooks/analytics/useAnalytics";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, documentsAuthCredentials } from "services/api.service";
import { Document } from "types/document";
import { saveToFile } from "utils/files";

export interface DownloadTask {
  taskId: number;
  documentUuid: string;
  bytesLoaded: number;
  downloadProgress: number;
  running: boolean;
  error?: any;
  fileCache?: Blob;
}

export default function useDocuments() {
  const [searchParams] = useSearchParams();
  const [meta, setMeta] = useState<{ totalPages: number; count: number }>({ totalPages: 0, count: 0 });
  const [documents, setDocuments] = useState<Document[]>();
  const [downloadTasks, setDownloadTasks] = useState<DownloadTask[]>([]);
  const { createAnalyticsEvent } = useAnalytics();

  useEffect(() => {
    api.get(`/documents?${searchParams.toString()}`).then((res) => {
      setDocuments(res.data.documents);
      setMeta(res.data.meta);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const updateTaskProgress = (data: DownloadTask) =>
    setDownloadTasks((prevTasks) => prevTasks.map((task) => (task.taskId === data.taskId ? data : task)));

  const downloadDocument = async (uuid: string, name: string, type: string, fileType: string) => {
    const task: DownloadTask = {
      taskId: Date.now(),
      documentUuid: uuid,
      bytesLoaded: 0,
      downloadProgress: 0,
      running: true,
    };
    const downloadEvent = type ? `downloaded_cod-${type}` : "downloaded_cod";
    createAnalyticsEvent(downloadEvent);
    setDownloadTasks((prevTasks) => [...prevTasks, task]);

    try {
      await api.get(`/documents/${uuid}`).then(({ data }) => {
        const documentUrl: string = data.document.url;
        const useAuthCredentials = documentUrl.includes(document.location.host);

        return axios
          .get(documentUrl, {
            ...(useAuthCredentials ? documentsAuthCredentials : {}),
            responseType: "blob",
            onDownloadProgress: (downloadProgress) => {
              task.bytesLoaded = downloadProgress.loaded;
              task.downloadProgress = downloadProgress.total ? downloadProgress.loaded / downloadProgress.total : 0;
              updateTaskProgress(task);
            },
          })
          .then(({ data }) => {
            // Caching the file to save some bandwidth on redownloads!
            task.fileCache = data;
            saveToFile(data, name, fileType);
          });
      });
    } catch (error) {
      task.error = error;
    } finally {
      task.running = false;
      updateTaskProgress(task);
    }
  };

  return {
    documents,
    meta,
    downloadDocument,
    downloadTasks,
  };
}
